import { checkoutPayFlowController } from '@cart-services/core/checkoutPayFlowController'
import { type CreditCardDialogContext } from '@cart-services/hooks/credit-card-dialog-types'
import { type ApplePayDialogContext } from '@cart-services/hooks/useApplePayDialogActions'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type FpPaymentTransactions } from '@hooks-api/types/dataModel/fortuneCampaign'
import { type BaseCheckoutPayFlowModule } from '@payments/lib/checkout-pay/BaseCheckoutPayFlowModule'
import { PAYMENT_DIALOG_TYPE } from '@payments/lib/checkout-pay/payment-dialog-type'
import { useEffect, useState } from 'react'

export interface UseFpCampaignCkoFlowPaymentParams {
  /** 非 CKO Flow 的 module 由 caller 篩成 null（見 isCkoFlowModule 用法）；為 null 時 execute 等函式直接 no-op */
  paymentModule: BaseCheckoutPayFlowModule | null
  rememberPaymentCardToken: boolean
  billingAddressEmail: string | undefined
  buildSuccessUrl: (orderHash: string) => string
  buildFallbackUrl: (orderHash: string) => string
  createCampaignCartTransaction: (headers?: Record<string, string>) => Promise<FpPaymentTransactions>
}

type CampaignPendingTransaction = Pick<FpPaymentTransactions, 'id' | 'order_hash'>

export interface CampaignCkoFlowExecuteArgs {
  paymentOptionId: number
  headers?: Record<string, string>
  /** 代付款帶既有待付款訂單；購物車省略時 hook 自建單 */
  existingTransaction?: CampaignPendingTransaction
}

export interface OpenCreditCardDialogArgs {
  paymentTransactionId: number
  orderHash: string
  paymentOptionId: number
}

/**
 * Campaign CKO Flow 付款路線匯流 hook。交易單來源：existingTransaction（代付款）或建單（購物車）。內部分流：
 * - Apple Pay → 帶交易單 id 開 dialog（授權 + execute 由 dialog 處理）
 * - 信用卡 → 帶交易單 id + 建單金額 開 dialog（init / submit / execute 由 dialog 處理）
 * Caller 仍須處理 token-based（非 Flow）路線。
 */
export const useFpCampaignCkoFlowPayment = (params: UseFpCampaignCkoFlowPaymentParams) => {
  const {
    paymentModule,
    rememberPaymentCardToken,
    billingAddressEmail,
    buildSuccessUrl,
    buildFallbackUrl,
    createCampaignCartTransaction,
  } = params

  const [applePayDialogContext, setApplePayDialogContext] = useState<ApplePayDialogContext | null>(null)
  const [creditCardDialogContext, setCreditCardDialogContext] = useState<CreditCardDialogContext | null>(null)

  useEffect(function bindCheckoutPayFlowController() {
    if (!paymentModule) return
    const unbind = checkoutPayFlowController.bind(paymentModule)
    return unbind
  }, [paymentModule])

  // 新單與既有待付款單共用同一條信用卡 dialog 組裝：差別僅「剛建單 vs 既有單」，context 形狀一致。
  const openCreditCardDialogForTransaction = useCallbackEvent((args: OpenCreditCardDialogArgs) => {
    setCreditCardDialogContext({
      paymentTransactionId: args.paymentTransactionId,
      orderHash: args.orderHash,
      paymentSuccessUrl: buildSuccessUrl(args.orderHash),
      paymentFallbackUrl: buildFallbackUrl(args.orderHash),
      paymentOptionId: args.paymentOptionId,
      email: billingAddressEmail || undefined,
      rememberPaymentCardToken,
    })
  })

  const execute = useCallbackEvent(async (args: CampaignCkoFlowExecuteArgs) => {
    if (!paymentModule) return

    const transaction = args.existingTransaction ?? await createCampaignCartTransaction(args.headers)
    const paymentTransactionId = Number(transaction.id)
    const orderHash = transaction.order_hash
    const fullPaymentSuccessUrl = buildSuccessUrl(orderHash)
    const fullPaymentFallbackUrl = buildFallbackUrl(orderHash)

    const dialogType = paymentModule.getPaymentDialogType()

    if (dialogType === PAYMENT_DIALOG_TYPE.APPLE_PAY) {
      setApplePayDialogContext({
        paymentTransactionId,
        orderHash,
        paymentSuccessUrl: fullPaymentSuccessUrl,
        paymentFallbackUrl: fullPaymentFallbackUrl,
        paymentOptionId: args.paymentOptionId,
      })
      return
    }

    if (dialogType === PAYMENT_DIALOG_TYPE.CREDIT_CARD) {
      openCreditCardDialogForTransaction({
        paymentTransactionId,
        orderHash,
        paymentOptionId: args.paymentOptionId,
      })
      return
    }

    // campaign CKO 目前僅支援 card / Apple Pay，其餘 module 明確 throw，避免誤落入信用卡分支 submit
    throw new Error(`[useFpCampaignCkoFlowPayment] no dialog available for module: ${paymentModule.constructor.name}`)
  })

  const closeApplePayDialog = useCallbackEvent(() => {
    setApplePayDialogContext(null)
  })

  const closeCreditCardDialog = useCallbackEvent(() => {
    setCreditCardDialogContext(null)
  })

  return {
    execute,
    openCreditCardDialogForTransaction,
    applePayDialogContext,
    closeApplePayDialog,
    creditCardDialogContext,
    closeCreditCardDialog,
  }
}
